import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import './Orders.scss';
import axios from "axios";
import { API_BASE_URL } from '../../config';
import _ from "lodash";

import returnIcon from "../../Cart/Assets/returnIcon.svg";
import rupeesIcon from "../Assets/rupeesIcon.svg";
import OrderStatusIcons from './OrderStatusIcons';
import { getOrderItemVisibleOptions } from '../getOrderItemVisibleOptions';
import { ORDERS_SEARCH_DEBOUNCE_MS } from '../ordersConstants';
import { useToast } from '../../Contexts/ToastContext';
import { mapOrderItemFromApi } from '../../utils/orderPayload';
import { ensureMediaUrl } from '../../utils/mediaUrl';
import { MobBreadcrumbs } from '../../Account/Mob/MobBreadcrumbs';
import { getOrderTracking } from '../../api/delivery';

const MOB_ORDERS_BREADCRUMBS = [
    { label: 'Home', path: '/' },
    { label: 'My Orders', path: null },
];

function formatDeliveryStatusLabel(raw) {
    if (raw == null || raw === '') return '—';
    const s = String(raw).replace(/_/g, ' ').trim();
    const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function MobOrders() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [orderData, setOrderData] = useState([]);
    const [totalOrderItemsCount, setTotalOrderItemsCount] = useState(0);
    const [searchVal, setSearchVal] = useState('');
    const [debouncedSearchVal, setDebouncedSearchVal] = useState('');
    const searchDebounceTimerRef = useRef(null);
    const [filterStatus, setFilterStatus] = useState({ OnTheWay: false, Delivered: false, Cancelled: false, Returned: false });
    const [filterTime, setFilterTime] = useState({ last_30_days: false, 2024: false });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [noMoreProducts, setNoMoreProducts] = useState(false);
    const [startRef, setStartRef] = useState(false);
    const observer = useRef();
    const initialFetchDoneRef = useRef(false);

    // Modals: cancel-confirm | track | review
    const [modalType, setModalType] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [trackLoading, setTrackLoading] = useState(false);
    const [trackInfo, setTrackInfo] = useState(null);
    // Item-level cancel: track cancelled item IDs (row id)
    const [cancelledItemIds, setCancelledItemIds] = useState([]);

    const debouncedLoadMore = useCallback(
        _.debounce(() => {
            fetchOrders()
        }, 500),
        [page]
    );

    const lastProductRef = useCallback((node) => {
        if (loading || noMoreProducts || !startRef) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setPage((prevPage) => prevPage + 1);
            }
        });

        if (node) {
            observer.current.observe(node);
            if (node.getBoundingClientRect().top < 0) return
            const isElementVisible = node.getBoundingClientRect().top < window.innerHeight;
            if (isElementVisible) {
                setPage((prevPage) => prevPage + 1);
            }
        }
    }, [loading, noMoreProducts, startRef]);

    async function fetchOrders() {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const filteredStatus = Object.keys(filterStatus).filter((key) => filterStatus[key]).map((key) => key);
            const filteredTime = Object.keys(filterTime).filter((key) => filterTime[key]).map((key) => key);

            const response = await axios.post(
                `${API_BASE_URL}/orders/get-all`,
                {
                    filterStatus: filteredStatus,
                    filterTime: filteredTime,
                    searchVal: debouncedSearchVal,
                    page: page,
                    size: 10
                },
                config
            );

            const { orders = [], totalOrderItemsCount: totalCount = 0 } = response.data || {};
            console.log('Mobile Orders response data - ', response.data);

            setTotalOrderItemsCount(totalCount);

            // Check if we got fewer orders than requested (indicating no more data)
            const requestedSize = 10;
            if (orders.length < requestedSize) {
                setNoMoreProducts(true);
            }

            // Backend returns a flat list of order items (each with product_data and delivery per item)
            const formatDeliveryDate = (dateString) => {
                if (!dateString) return null;
                try {
                    const date = new Date(dateString);
                    const day = date.getDate();
                    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                    const month = months[date.getMonth()];
                    return `${day} ${month}`;
                } catch (e) {
                    return null;
                }
            };

            const processedOrders = orders.map((item) => {
                const mapped = mapOrderItemFromApi(item, API_BASE_URL);
                return {
                    ...mapped,
                    deliveryDate: formatDeliveryDate(mapped.deliveryDate),
                };
            });

            console.log('Mobile Processed orders - ', processedOrders);

            // Dedupe by item id so we never show the same item twice (e.g. double fetch or pagination overlap)
            const deduped = processedOrders.filter((item, idx, arr) => arr.findIndex((i) => i.id === item.id) === idx);

            if (page === 1) {
                setOrderData(deduped);
            } else {
                setOrderData((prevOrderData) => {
                    const combined = [...prevOrderData, ...deduped];
                    const byId = new Map();
                    combined.forEach((item) => byId.set(item.id, item));
                    return Array.from(byId.values());
                });
            }
        }
        catch (error) {
            console.error("Failed to fetch orders:", error);
        }
        finally {
            setLoading(false);
            setStartRef(true);
        }
    }

    // Initial fetch on mount (ref prevents double call in React Strict Mode)
    useEffect(() => {
        if (initialFetchDoneRef.current) return;
        initialFetchDoneRef.current = true;
        setOrderData([]);
        setTotalOrderItemsCount(0);
        setPage(1);
        setNoMoreProducts(false);
        setStartRef(false);
        fetchOrders();
    }, []);

    // Fetch more when page changes (for pagination)
    useEffect(() => {
        if (page > 1 && startRef) {
            fetchOrders();
        }
    }, [page, startRef]);

    // Debounce search input → API query (same delay as web orders)
    useEffect(() => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
        }
        searchDebounceTimerRef.current = setTimeout(() => {
            searchDebounceTimerRef.current = null;
            setDebouncedSearchVal(searchVal);
        }, ORDERS_SEARCH_DEBOUNCE_MS);
        return () => {
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
                searchDebounceTimerRef.current = null;
            }
        };
    }, [searchVal]);

    useEffect(() => {
        // Reset pagination when filters or debounced search change
        if (startRef) { // Only refetch if initial load is done
            setOrderData([]);
            setTotalOrderItemsCount(0);
            setPage(1);
            setNoMoreProducts(false);
            fetchOrders();
        }
    }, [filterStatus, filterTime, debouncedSearchVal]);

    const closeModal = () => {
        setModalType(null);
        setSelectedOrder(null);
        setTrackInfo(null);
        setTrackLoading(false);
    };

    const openCancelConfirm = (order, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        setSelectedOrder(order);
        setModalType('cancel-confirm');
    };

    const openTrack = async (order, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        setSelectedOrder(order);
        setModalType('track');
        setTrackInfo(null);
        setTrackLoading(true);
        try {
            const data = await getOrderTracking(order.orderId);
            setTrackInfo(data);
        } finally {
            setTrackLoading(false);
        }
    };

    const openReview = (order, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        navigate('/orders/feedback', { state: { orderItem: order } });
    };

    const openViewReview = (order, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        setSelectedOrder(order);
        setModalType('view-review');
    };

    const handleCancelItemConfirm = async () => {
        if (!selectedOrder?.orderId || !selectedOrder?.orderItemId) return;
        setCancelLoading(true);
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.post(
                '/orders/cancel-item',
                {
                    orderId: selectedOrder.orderId,
                    product_id: selectedOrder.orderItemId,
                    selectedSize: selectedOrder.size || selectedOrder.selectedSize
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (data?.success) {
                setCancelledItemIds((prev) => (prev.includes(selectedOrder.id) ? prev : [...prev, selectedOrder.id]));
                closeModal();
            } else {
                showToast({ message: data?.message || 'Failed to cancel item' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to cancel item';
            showToast({ message: msg });
        } finally {
            setCancelLoading(false);
        }
    };

    function handleStatusChange(key) {
        setFilterStatus({ ...filterStatus, [key]: !filterStatus[key] });
    }

    function handleTimeChange(key) {
        if (key === "last_30_days") {
            setFilterTime({ '2024': false, [key]: !filterTime[key] });
        }
        else if (key === "2024") {
            setFilterTime({ 'last_30_days': false, [key]: !filterTime[key] });
        }
    }

    function handleSearchChange(val) {
        setSearchVal(val);
    }

    const searchProducts = () => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
            searchDebounceTimerRef.current = null;
        }
        setOrderData([]);
        setTotalOrderItemsCount(0);
        setPage(1);
        setNoMoreProducts(false);
        if (searchVal === debouncedSearchVal) {
            setStartRef(false);
            fetchOrders();
        } else {
            setDebouncedSearchVal(searchVal);
        }
    };

    return (
        <div className="mob-orders-scaling-root">
        <div className="mob-orders-container">
            {/* Icon Showcase - Remove this section after choosing your preferred icon set */}

            <MobBreadcrumbs items={MOB_ORDERS_BREADCRUMBS} />

            <div className="content-main">
                <div className="mobile-filters-container">
                    <div className="mobile-filter-wrapper">
                        <div className="mobile-filter-section-header">
                            <div className="mobile-filter-title">
                                <span>Filters</span>
                            </div>
                        </div>
                        <div className="mobile-filter-group">
                            <div className="mobile-filter-group-title">Order Status</div>
                            <div className="mobile-filter-option" data-val="On_the_way">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterStatus.OnTheWay}
                                        onChange={() => handleStatusChange('OnTheWay')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">On the way</div>
                            </div>
                            <div className="mobile-filter-option" data-val="Delivered">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterStatus.Delivered}
                                        onChange={() => handleStatusChange('Delivered')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">Delivered</div>
                            </div>
                            <div className="mobile-filter-option" data-val="Cancelled">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterStatus.Cancelled}
                                        onChange={() => handleStatusChange('Cancelled')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">Cancelled</div>
                            </div>
                            <div className="mobile-filter-option" data-val="Returned">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterStatus.Returned}
                                        onChange={() => handleStatusChange('Returned')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">Returned</div>
                            </div>
                        </div>
                        <div className="mobile-filter-group">
                            <div className="mobile-filter-group-title">Order Time</div>
                            <div className="mobile-filter-option" data-val="Last_30_days">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterTime.last_30_days}
                                        onChange={() => handleTimeChange('last_30_days')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">Last 30 days</div>
                            </div>
                            <div className="mobile-filter-option" data-val="2024">
                                <label className="mobile-filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterTime[2024]}
                                        onChange={() => handleTimeChange('2024')}
                                    />
                                    <div className="mobile-filter-checkbox-custom"></div>
                                </label>
                                <div className="mobile-filter-option-text">2024</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="column mobile-main-content">
                    <div className="mobile-search-container">
                        <div className="mobile-search-input-container">
                            <input
                                type="text"
                                className="mobile-search-input"
                                placeholder="Search your orders here"
                                value={searchVal}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                fdprocessedid="ma9z58"
                            />
                        </div>
                        <button className="mobile-search-button" onClick={searchProducts} fdprocessedid="ajv5wl">
                            Search Orders
                        </button>
                    </div>
                    <div className="mobile-results-count">
                        {totalOrderItemsCount === 0
                            ? '0 items found'
                            : `${totalOrderItemsCount} ${totalOrderItemsCount === 1 ? 'item' : 'items'} found`}
                    </div>
                    <div>
                        {orderData.map((order, index) => {
                            // Helper function to determine timeline status based on actual API order data
                            const getTimelineStatus = (orderStatus, deliveryStatus) => {
                                // Normalize API values to lowercase for comparison
                                const status = (orderStatus || '').toLowerCase();
                                const delivery = (deliveryStatus || '').toLowerCase();

                                // Check final states first
                                const isDelivered = status === 'delivered' ||
                                    delivery === 'delivered' ||
                                    delivery === 'completed';
                                const isOnTheWay = delivery === 'ontheway' ||
                                    delivery === 'on the way' ||
                                    delivery === 'out for delivery';
                                const isShipped = status === 'shipped' ||
                                    delivery === 'shipped' ||
                                    isOnTheWay ||
                                    isDelivered;

                                // Processed: Always completed if order exists (order is created)
                                const processedState = 'completed';

                                // Shipped: Completed if shipped, on the way, or delivered
                                const shippedState = isShipped ? 'completed' : 'pending';

                                // On the Way: Active if currently on the way, completed if delivered
                                const onTheWayState = isDelivered ? 'completed' : (isOnTheWay ? 'active' : 'pending');

                                // Delivered: Completed if delivered, otherwise pending
                                const deliveredState = isDelivered ? 'completed' : 'pending';

                                return {
                                    processed: processedState,
                                    shipped: shippedState,
                                    onTheWay: onTheWayState,
                                    delivered: deliveredState
                                };
                            };

                            const timelineStatus = getTimelineStatus(order.itemStatus, order.deliveryStatus);
                            const visibleOptions = getOrderItemVisibleOptions(order);
                            const discountPct = order.mrp > 0 && order.price != null
                                ? Math.round((1 - order.price / order.mrp) * 100)
                                : 0;

                            return (
                                <div
                                    key={order.id || index}
                                    className="mobile-order-item"
                                    ref={index === orderData.length - 1 ? lastProductRef : null}
                                >
                                    <a
                                        className="mobile-order-link"
                                        href={`/order_details?order_id=${order.id}&item_id=${order.id}&unit_id=${order.id}000`}
                                    >
                                        <div className="row">
                                            <div className="top-container">
                                                <div className="cartItemsList">
                                                    <div className="itemContainer-base-itemMargin">
                                                        <div className="itemContainer-base-item">
                                                            <div className="itemContainer-base-itemLeft">
                                                                <img src={order.urls && order.urls.length > 0 ? ensureMediaUrl(order.urls[0]) : ''} alt={order.title} />
                                                            </div>

                                                            <div className="itemContainer-base-itemCenter">
                                                                <div className="brand-and-seller">
                                                                    <div>{order.brand}</div>
                                                                    <a>{order.title}</a>
                                                                </div>

                                                                <div className="rating-container">
                                                                    <div className="overall-rating">
                                                                        <div>3.7</div>
                                                                        <span className="myntraweb-sprite"></span>
                                                                        <div className="index-separator">|</div>
                                                                        <div>480 Ratings</div>
                                                                    </div>
                                                                </div>

                                                                <div className="itemContainer-base-sizeAndQtyContainer">
                                                                    <div className="itemContainer-base-sizeAndQty">
                                                                        <div className="itemComponents-base-size">
                                                                            <span>Size: {order.size}</span>
                                                                        </div>

                                                                        <div className="itemComponents-base-quantity">
                                                                            <span>Qty: {order.qty}</span>
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="6"
                                                                                height="3"
                                                                                viewBox="0 0 6 3"
                                                                                className="itemComponents-base-dropDown"
                                                                            >
                                                                                <path fillRule="evenodd" d="M0 0h6L3 3z"></path>
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="itemContainer-base-price">
                                                                    <div>
                                                                        ₹ {order.price}
                                                                    </div>

                                                                    <div>
                                                                        <span>{order.mrp}</span>
                                                                        {discountPct > 0 && (
                                                                            <span>{discountPct}% OFF</span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="returnPeriod-base-returnItem">
                                                                    <div className="returnPeriod-base-returnIcon">
                                                                        <img src={returnIcon} />
                                                                    </div>
                                                                    <div className="returnPeriod-base-returnText">
                                                                        <span>14 days </span>
                                                                        return available
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="itemContainer-base-itemRight">
                                                                <div className="delivery-side-details">
                                                                    <div className="vertical-timeline-classic">
                                                                        <div className="timeline-vertical">
                                                                            <div className={`timeline-item ${timelineStatus.processed}`}>
                                                                                <div className="timeline-dot">
                                                                                    <OrderStatusIcons status="processed" />
                                                                                </div>
                                                                                <div className="timeline-content">
                                                                                    <span className="status-label">Processed</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className={`timeline-line ${timelineStatus.processed}`}></div>
                                                                            <div className={`timeline-item ${timelineStatus.shipped}`}>
                                                                                <div className="timeline-dot">
                                                                                    <OrderStatusIcons status="shipped" />
                                                                                </div>
                                                                                <div className="timeline-content">
                                                                                    <span className="status-label">Shipped</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className={`timeline-line ${timelineStatus.shipped === 'completed' ? 'completed' : (timelineStatus.onTheWay === 'active' || timelineStatus.onTheWay === 'completed') ? 'active' : 'pending'}`}></div>
                                                                            <div className={`timeline-item ${timelineStatus.onTheWay}`}>
                                                                                <div className="timeline-dot">
                                                                                    <OrderStatusIcons status="on-the-way" />
                                                                                </div>
                                                                                <div className="timeline-content">
                                                                                    <span className="status-label">On The Way</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className={`timeline-line ${timelineStatus.onTheWay === 'completed' ? 'completed' : (timelineStatus.delivered === 'active' || timelineStatus.delivered === 'completed') ? 'active' : 'pending'}`}></div>
                                                                            <div className={`timeline-item ${timelineStatus.delivered}`}>
                                                                                <div className="timeline-dot">
                                                                                    <OrderStatusIcons status="delivered" />
                                                                                </div>
                                                                                <div className="timeline-content">
                                                                                    <span className="status-label">
                                                                                        {order.deliveryDate || 'Deliver'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a>

                                    <div className="options">
                                        {cancelledItemIds.includes(order.id) || (order.itemStatus || '').toString().toLowerCase() === 'cancelled' ? (
                                            <button type="button" className="option-card option-card-cancelled" disabled>
                                                <span className="option-title">Cancelled</span>
                                            </button>
                                        ) : (
                                            visibleOptions.map((key) => {
                                                const label = key === 'cancel' ? 'Cancel' : key === 'track' ? 'Track' : key === 'view-review' ? 'View feedback' : 'Feedback';
                                                const optionClass = `option-card option-card-${key === 'view-review' ? 'review' : key}`;
                                                const onOptionClick = (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (key === 'cancel') openCancelConfirm(order, e);
                                                    else if (key === 'track') openTrack(order, e);
                                                    else if (key === 'view-review') openViewReview(order, e);
                                                    else openReview(order, e);
                                                };
                                                const clearPressed = (e) => {
                                                    e.currentTarget.classList.remove('is-pressed');
                                                };
                                                return (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        className={optionClass}
                                                        onPointerDown={(e) => {
                                                            e.currentTarget.classList.add('is-pressed');
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onPointerUp={clearPressed}
                                                        onPointerLeave={clearPressed}
                                                        onPointerCancel={clearPressed}
                                                        onClick={onOptionClick}
                                                    >
                                                        <span className="option-title">{label}</span>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    {loading && (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Loading more orders...</div>
                        </div>
                    )}
                    {!loading && orderData.length === 0 && (
                        <div className="mobile-orders-empty-state">
                            <div className="empty-orders-container">
                                <div className="empty-orders-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                                        <path d="M9 14l2 2 4-4"></path>
                                    </svg>
                                </div>
                                <h2 className="empty-orders-title">No orders found</h2>
                                <p className="empty-orders-message">You haven&apos;t placed any orders yet. Start shopping to see your orders here!</p>
                                <button
                                    className="empty-orders-button"
                                    onClick={() => navigate('/')}
                                >
                                    Start Shopping
                                </button>
                            </div>
                        </div>
                    )}
                    {!loading && orderData.length > 0 && (
                        <div className="mobile-no-more-results">
                            <span>No more results to display</span>
                        </div>
                    )}

                    {/* Order action modals */}
                    {modalType && (
                        <div className="orders-modal-overlay" onClick={closeModal}>
                            <div className="orders-modal" onClick={(e) => e.stopPropagation()}>
                                {modalType === 'cancel-confirm' && selectedOrder && (
                                    <>
                                        <div className="orders-modal-header">
                                            <h3 className="orders-modal-title">Cancel item?</h3>
                                            <button type="button" className="orders-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                                        </div>
                                        <div className="orders-modal-body">
                                            <p>Cancel this item? This action cannot be undone.</p>
                                        </div>
                                        <div className="orders-modal-footer">
                                            <button type="button" className="orders-modal-btn orders-modal-btn-secondary" onClick={closeModal}>Back</button>
                                            <button type="button" className="orders-modal-btn orders-modal-btn-primary" onClick={handleCancelItemConfirm} disabled={cancelLoading}>
                                                {cancelLoading ? 'Cancelling…' : 'Yes, cancel item'}
                                            </button>
                                        </div>
                                    </>
                                )}
                                {modalType === 'track' && selectedOrder && (
                                    <>
                                        <div className="orders-modal-header">
                                            <h3 className="orders-modal-title" id="orders-modal-delivery-title">Delivery info</h3>
                                            <button type="button" className="orders-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                                        </div>
                                        <div className="orders-modal-body orders-modal-body--delivery" role="region" aria-labelledby="orders-modal-delivery-title">
                                            <p className="orders-delivery-product">{selectedOrder.title}</p>
                                            <dl className="orders-delivery-dl">
                                                <div className="orders-delivery-row">
                                                    <dt>Status</dt>
                                                    <dd>
                                                        <span className="orders-delivery-badge">
                                                            {formatDeliveryStatusLabel(
                                                                trackInfo?.tracking?.currentStatus ||
                                                                selectedOrder.deliveryStatus ||
                                                                selectedOrder.itemStatus
                                                            )}
                                                        </span>
                                                    </dd>
                                                </div>
                                                <div className="orders-delivery-row">
                                                    <dt>Expected delivery</dt>
                                                    <dd>{selectedOrder.deliveryDate || '—'}</dd>
                                                </div>
                                                {(trackInfo?.shipping?.awb || selectedOrder.awb) && (
                                                    <div className="orders-delivery-row">
                                                        <dt>AWB</dt>
                                                        <dd>{trackInfo?.shipping?.awb || selectedOrder.awb}</dd>
                                                    </div>
                                                )}
                                                {(trackInfo?.shipping?.courier_name || selectedOrder.courierName) && (
                                                    <div className="orders-delivery-row">
                                                        <dt>Courier</dt>
                                                        <dd>{trackInfo?.shipping?.courier_name || selectedOrder.courierName}</dd>
                                                    </div>
                                                )}
                                            </dl>
                                            {trackLoading && <p>Refreshing live tracking…</p>}
                                            {!trackLoading && trackInfo?.message && !trackInfo?.tracking && (
                                                <p>{trackInfo.message}</p>
                                            )}
                                            {Array.isArray(trackInfo?.tracking?.activities) && trackInfo.tracking.activities.length > 0 && (
                                                <ul className="orders-delivery-activities">
                                                    {trackInfo.tracking.activities.slice(0, 8).map((a, i) => (
                                                        <li key={`${a.date || ''}-${i}`}>
                                                            {a.date ? `${a.date} — ` : ''}{a.activity}
                                                            {a.location ? ` (${a.location})` : ''}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {(trackInfo?.tracking?.trackUrl || trackInfo?.shipping?.tracking_url || selectedOrder.trackingUrl) && (
                                                <p>
                                                    <a
                                                        href={trackInfo?.tracking?.trackUrl || trackInfo?.shipping?.tracking_url || selectedOrder.trackingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Open courier tracking
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                        <div className="orders-modal-footer orders-modal-footer--delivery-actions orders-modal-footer--delivery-close-only">
                                            <button type="button" className="orders-modal-action-btn" onClick={closeModal}>
                                                Close
                                            </button>
                                        </div>
                                    </>
                                )}
                                {modalType === 'view-review' && selectedOrder && selectedOrder.reviewData && (
                                    <>
                                        <div className="orders-modal-header">
                                            <h3 className="orders-modal-title">Your feedback</h3>
                                            <button type="button" className="orders-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                                        </div>
                                        <div className="orders-modal-body">
                                            <p className="orders-modal-product">{selectedOrder.title}</p>
                                            <div className="orders-modal-rating orders-modal-rating-readonly">
                                                <span>Rating:</span>
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <span key={n} className={`orders-modal-star ${Number(selectedOrder.reviewData.rating) >= n ? 'active' : ''}`}>★</span>
                                                ))}
                                            </div>
                                            {selectedOrder.reviewData.comment ? (
                                                <p className="orders-modal-review-comment">{selectedOrder.reviewData.comment}</p>
                                            ) : null}
                                            {selectedOrder.reviewData.images && selectedOrder.reviewData.images.length > 0 ? (
                                                <div className="orders-modal-review-images">
                                                    {selectedOrder.reviewData.images.map((src, i) => (
                                                        <img key={i} src={src} alt={`Review ${i + 1}`} className="orders-modal-review-thumb" />
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="orders-modal-footer">
                                            <button type="button" className="orders-modal-btn orders-modal-btn-primary" onClick={closeModal}>Close</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </div>
    );
}
